/**
 * /admin/reels/acceso — password, access mode, view limits (light config fetch).
 */
import { useCallback, useEffect, useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { AccessMode, PrivateReelsConfig } from "./types";

export default function PrivateReelsAccessPage() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<PrivateReelsConfig | null>(null);
  const [password, setPassword] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/private-reels?light=1", { headers });
      const data = (await res.json()) as {
        ok?: boolean;
        config?: PrivateReelsConfig;
        error?: string;
      };
      if (!res.ok || !data.ok || !data.config) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setConfig(data.config);
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      // Access-only fields — never send playlist keys (would clobber concurrent
      // Videos-page edits with a stale light fetch of curated_playback_ids).
      const body: Record<string, unknown> = {
        enabled: config.enabled,
        access_mode: config.access_mode,
        max_views_per_session: config.max_views_per_session,
        // Don't re-list Mux assets on access-only saves
        skip_resolve: true,
      };
      if (password.trim().length >= 4) body.password = password.trim();
      const res = await fetch("/api/admin/private-reels", {
        method: "PUT",
        headers,
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        config?: PrivateReelsConfig;
        error?: string;
      };
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setConfig(data.config || config);
      setPassword("");
      toast({ title: "Guardado", description: "Acceso actualizado" });
    } catch (e) {
      toast({
        title: "Error",
        description: e instanceof Error ? e.message : "Error",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando acceso…
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-white/[0.08] bg-zinc-900/40 p-5 space-y-4">
      <h2 className="text-sm font-semibold">Acceso a /reels</h2>

      <div className="flex items-center justify-between gap-4">
        <div>
          <Label>Habilitado</Label>
          <p className="text-[11px] text-muted-foreground">
            Si está off, /reels no entrega videos.
          </p>
        </div>
        <Switch
          checked={config.enabled}
          onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Modo de acceso</Label>
          <Select
            value={config.access_mode}
            onValueChange={(v) =>
              setConfig({ ...config, access_mode: v as AccessMode })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="password_or_link">Password o link</SelectItem>
              <SelectItem value="password">Solo password</SelectItem>
              <SelectItem value="link_only">Solo link expirable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>
            Nueva contraseña{" "}
            {config.has_password ? (
              <Badge variant="outline" className="ml-1 text-[10px]">
                ya hay password
              </Badge>
            ) : null}
          </Label>
          <Input
            type="password"
            autoComplete="new-password"
            placeholder="Mín. 4 caracteres (vacío = no cambiar)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5 max-w-xs">
        <Label>Máx. vistas por sesión (0 = sin límite)</Label>
        <Input
          type="number"
          min={0}
          max={500}
          value={config.max_views_per_session}
          onChange={(e) =>
            setConfig({
              ...config,
              max_views_per_session: Number(e.target.value) || 0,
            })
          }
        />
      </div>

      <Button type="button" onClick={() => void save()} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
        Guardar acceso
      </Button>
    </section>
  );
}
